import { APP_LANGUAGES, type ButtonProps, type Language } from "@/types";
import { cn } from "@/utils/cn";
import { useApp } from "@h/useCtx";
import { useHoverMenu } from "@h/useHoverMenu";
import { useIsMobile } from "@h/useResponsive";
import Button from "@ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ui/dropdown-menu";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import ResponsiveSheet from "../ui/responsive-sheet";

interface LangDropdownProps {
  design?: ButtonProps["design"];
  className?: string;
  floatingPosition?: ButtonProps["floatingPosition"];
}

export default function LangDropdown({
  design = "outline",
  className,
  floatingPosition = "bottom",
}: LangDropdownProps) {
  const { language, setLanguage } = useApp();
  const { t } = useTranslation();
  const { open, setOpen, hoverProps, contentProps } = useHoverMenu();
  const isMobile = useIsMobile();

  const handleLanguageChange = (langCode: Language) => () => {
    setLanguage(langCode);
    setOpen(false);
  };

  // Single source for a language row — rendered as a DropdownMenuItem on
  // desktop (pointer UX) or a plain button inside the mobile ResponsiveSheet.
  const renderLang = (
    lang: (typeof APP_LANGUAGES)[number],
    asMenu: boolean,
  ) => {
    const isSelected = language === lang.code;
    const base = cn(
      "flex items-center justify-between rounded-xl transition-colors",
      isSelected
        ? "bg-primary/10 text-primary"
        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
    );
    const inner = (
      <>
        <span className="text-sm font-medium">{t(lang.label)}</span>
        {isSelected && <Check size={14} strokeWidth={3} />}
      </>
    );
    return asMenu ? (
      <DropdownMenuItem
        key={lang.code}
        onClick={handleLanguageChange(lang.code as Language)}
        className={cn(
          base,
          "px-3 py-2 cursor-pointer focus:bg-accent focus:text-accent-foreground",
        )}
      >
        {inner}
      </DropdownMenuItem>
    ) : (
      <button
        key={lang.code}
        type="button"
        onClick={handleLanguageChange(lang.code as Language)}
        className={cn(base, "w-full px-4 py-3")}
      >
        {inner}
      </button>
    );
  };

  // Trigger compartido — muestra el código del idioma activo (EN / ES / …)
  // en vez de un globo estático, así el idioma actual se ve de un vistazo.
  const trigger = (
    <Button
      design={design}
      size="icon"
      floatingLabel={t("language")}
      floatingPosition={floatingPosition}
      // must contain the visible text ("ES") or axe flags
      // label-content-name-mismatch (WCAG 2.5.3 Label in Name)
      aria-label={`${t("language")}: ${language.slice(0, 2).toUpperCase()}`}
      className={className}
      onClick={() => setOpen(true)}
      {...hoverProps}
    >
      <span className="font-mono text-xs uppercase tracking-wide">
        {language.slice(0, 2)}
      </span>
    </Button>
  );

  // En escritorio, mantenemos el Dropdown para mejor UX de puntero
  if (!isMobile) {
    return (
      // modal={false}: a modal Radix menu traps focus and walks the whole
      // document to aria-hide every sibling on open. On the big landing pages
      // that was ~300ms of handler time per open (measured, 4x CPU throttle) —
      // the app's worst interaction. A language menu needs neither.
      <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          {...contentProps}
          align="end"
          className="w-48 rounded-2xl p-2"
        >
          <DropdownMenuLabel className="label-micro text-muted-foreground px-2 py-2">
            {t("language")}
          </DropdownMenuLabel>
          {/* Semantic separator color */}
          <DropdownMenuSeparator className="bg-border" />
          <div className="flex flex-col gap-1 mt-1">
            {APP_LANGUAGES.map((lang) => renderLang(lang, true))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // En móvil/touch, usamos ResponsiveSheet
  return (
    <>
      {trigger}
      <ResponsiveSheet open={open} onOpenChange={setOpen} title={t("language")}>
        <div className="flex flex-col gap-1">
          {APP_LANGUAGES.map((lang) => renderLang(lang, false))}
        </div>
      </ResponsiveSheet>
    </>
  );
}
